import React, { useState, useEffect, useRef } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import Slider from "react-slick";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import httpClient from "../../utils/httpClients";
import { usePlayer } from "../../../PlayerContext";

function Song() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [song, setSong] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [genres, setGenres] = useState([]);
  const [openDropdown, setOpenDropdown] = useState(null);
  const [likedSongs, setLikedSongs] = useState(new Set());
  const [sharePopup, setSharePopup] = useState({ open: false, song: null });
  const [playlists, setPlaylists] = useState([]);
  const [playlistModal, setPlaylistModal] = useState({
    open: false,
    song: null,
  });
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [newPlaylist, setNewPlaylist] = useState({
    name: "",
    description: "",
    coverImage: null,
  });
  const [playlistLoading, setPlaylistLoading] = useState(false);
  const [selectedPlaylists, setSelectedPlaylists] = useState(new Set());

  const sliderRef = useRef(null);

  const { isPlaying, currentTrack, togglePlayPause, triggerQueueUpdate } =
    usePlayer();

  // FETCH GENRES + SONG
  useEffect(() => {
    const fetchAllData = async () => {
      try {
        setLoading(true);
        setError(null);

        const [genreRes, songRes] = await Promise.all([
          httpClient.get("/user/music/get-themes"),
          httpClient.get(`/user/music/media-track/${id}`),
        ]);

        if (genreRes.data?.success) {
          setGenres(genreRes.data.data.getThemes || []);
        }

        if (songRes.data?.success) {
          const data = songRes.data.data;
          setSong(data);
          if (data.isLiked) {
            setLikedSongs(new Set([data._id]));
          }
        } else {
          setError("Song not found");
        }
      } catch (err) {
        console.error("Fetch error:", err);
        setError("Failed to load data");
      } finally {
        setLoading(false);
      }
    };

    if (id) fetchAllData();
  }, [id]);

  // SMART QUEUE ACTION - Automatically sends trackId or playlistId
  const handleQueueAction = async (action, id, type = null) => {
    try {
      const payload = { action };

      if (type === "genresong") {
        payload.playlistId = id;
        payload.type = "genresong";
      } else {
        payload.trackId = id;
      }

      const res = await httpClient.post("user/queue/queue-controller", payload);
      if (res.data?.success) {
        triggerQueueUpdate();
        return true;
      }
    } catch (err) {
      console.error("Queue error:", err);
      return false;
    }
  };

  const playNow = async (trackId) => {
    try {
      const isSameSong = currentTrack?._id === trackId;

      if (isSameSong) {
        // Same song → just toggle play/pause → NO API CALL
        togglePlayPause();
        return;
      }

      // Different song → add to queue + play → API CALL
      const success = await handleQueueAction("addAndPlay", trackId);
      if (success && !isPlaying) {
        togglePlayPause(); // Force play if backend didn't auto-start
      }
    } catch (err) {
      console.error("Play now error:", err);
    }
  };

  // PLAY ALL FROM GENRE - Sends playlistId + type: "genre"
  const playGenreSongs = async (genreId) => {
    try {
      const success = await handleQueueAction(
        "addPlaylistAndPlay",
        genreId,
        "genresong",
      );
      navigate(`/party-music/${genreId}`);
      if (success) {
      }
    } catch (err) {
      alert("Failed to load genre songs");
    }
  };

  // LIKE SONG (optimistic update)
  const handleLikeSong = async (e) => {
    e.stopPropagation();
    if (!song) return;

    const isLiked = likedSongs.has(song._id);
    const newLiked = !isLiked;

    setLikedSongs((prev) => {
      const newSet = new Set(prev);
      isLiked ? newSet.delete(song._id) : newSet.add(song._id);
      return newSet;
    });
    setSong((prev) => ({ ...prev, isLiked: newLiked }));

    try {
      const res = await httpClient.post(
        `/user/play-history/like-content/${song._id}/${isLiked ? "dislike" : "like"}`,
        {},
        { skipToast: true, skipLoader: true },
      );
      if (!res.data?.success) {
        setLikedSongs((prev) => {
          const newSet = new Set(prev);
          newLiked ? newSet.delete(song._id) : newSet.add(song._id);
          return newSet;
        });
        setSong((prev) => ({ ...prev, isLiked }));
      }
    } catch (err) {
      console.error("Like error:", err);
      setLikedSongs((prev) => {
        const newSet = new Set(prev);
        newLiked ? newSet.delete(song._id) : newSet.add(song._id);
        return newSet;
      });
      setSong((prev) => ({ ...prev, isLiked }));
    }
  };

  // FORMAT DURATION
  const formatDuration = (seconds) => {
    if (!seconds) return "0:00";
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, "0")}`;
  };

  // DROPDOWN
  const toggleDropdown = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setOpenDropdown((prev) => (prev === "more" ? null : "more"));
  };

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (!e.target.closest(".more-dropdown")) {
        setOpenDropdown(null);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // PLAYLIST MODAL
  const openPlaylistModal = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    setPlaylistModal({ open: true, song });
    setSelectedPlaylists(new Set());
    setShowCreateForm(false);

    try {
      const res = await httpClient.get("/user/playlist/get-playlists");
      if (res.data?.success) {
        const fetched = res.data.data.playlist || [];
        setPlaylists(fetched);

        const alreadyIn = new Set();
        fetched.forEach((p) => {
          if (
            p.items?.some(
              (item) => item.itemId === song._id && item.itemType === "Music",
            )
          ) {
            alreadyIn.add(p._id);
          }
        });
        setSelectedPlaylists(alreadyIn);
      }
    } catch (err) {
      console.error(err);
    }
    setOpenDropdown(null);
  };

  const togglePlaylistSelection = (playlistId) => {
    setSelectedPlaylists((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(playlistId)) newSet.delete(playlistId);
      else newSet.add(playlistId);
      return newSet;
    });
  };

  const addToSelectedPlaylists = async () => {
    if (selectedPlaylists.size === 0) return;
    setPlaylistLoading(true);
    try {
      for (const pid of selectedPlaylists) {
        await httpClient.post(
          `/user/playlist/add-item?playlistId=${pid}&itemId=${song._id}&itemType=Music`,
        );
      }
      setPlaylistModal({ open: false, song: null });
      setShowCreateForm(false);
    } catch (err) {
      alert("Failed to add to some playlists");
    } finally {
      setPlaylistLoading(false);
    }
  };

  const createNewPlaylist = async () => {
    if (!newPlaylist.name.trim()) return alert("Playlist name required");

    setPlaylistLoading(true);
    const formData = new FormData();
    formData.append("name", newPlaylist.name);
    formData.append("description", newPlaylist.description);
    if (newPlaylist.coverImage)
      formData.append("coverImage", newPlaylist.coverImage);

    try {
      const res = await httpClient.post(
        "/user/playlist/create-playlist",
        formData,
        {
          headers: { "Content-Type": "multipart/form-data" },
        },
      );

      if (res.data?.success) {
        const newId = res.data.data.playlist._id;
        setSelectedPlaylists((prev) => new Set(prev).add(newId));
        await addToSelectedPlaylists();
      }
    } catch (err) {
      alert("Failed to create playlist");
    } finally {
      setPlaylistLoading(false);
    }
  };

  // SHARE FUNCTIONS
  const generateShareableUrl = (songId) => {
    return `${window.location.origin}music/${encodeURIComponent(songId)}`;
  };

  const shareOnSocialMedia = (platform) => {
    const text = encodeURIComponent(`Check out "${song.title}" on VolumeTV!`);
    const url = encodeURIComponent(generateShareableUrl(song._id));
    const links = {
      facebook: `https://www.facebook.com/sharer/sharer.php?u=${url}`,
      twitter: `https://twitter.com/intent/tweet?text=${text}&url=${url}`,
      whatsapp: `https://api.whatsapp.com/send?text=${text}%20${url}`,
      telegram: `https://t.me/share/url?url=${url}&text=${text}`,
    };
    window.open(links[platform], "_blank", "width=600,height=400");
  };

  const copyLink = async () => {
    const url = generateShareableUrl(song._id);
    try {
      await navigator.clipboard.writeText(url);
      alert("Link copied!");
    } catch {
      prompt("Copy this link:", url);
    }
  };

  const openShare = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setSharePopup({ open: true, song: { _id: song._id, title: song.title } });
    setOpenDropdown(null);
  };

  if (loading) {
    return (
      <div
        className="content-inner pb-0 container-fluid d-flex justify-content-center align-items-center"
        style={{ height: "70vh" }}
      >
        <div className="spinner-border text-primary" role="status">
          <span className="sr-only">Loading...</span>
        </div>
      </div>
    );
  }

  if (error || !song) {
    return (
      <div className="content-inner pb-0 container-fluid">
        <div className="alert alert-danger text-center">
          {error || "Song not found"}
        </div>
      </div>
    );
  }

  const releaseYear = new Date(song.releaseDate).getFullYear();
  const isCurrentSongPlaying = isPlaying && currentTrack?._id === song._id;

  return (
    <div className="content-inner pb-0 container-fluid" id="page_layout">
      <div className="row row--grid">
        {/* BREADCRUMB */}
        <div className="col-12">
          <ul className="breadcrumb">
            <li className="breadcrumb__item">
              <Link to="/">Home</Link>
            </li>
            <li className="breadcrumb__item breadcrumb__item--active">
              {song.title}
            </li>
          </ul>
        </div>

        {/* SONG HEADER */}
        <div className="col-12">
          <div className="release">
            <div className="release__content trending-song">
              <div className="release__cover">
                <img
                  src={
                    song.coverImageUrl ||
                    `${process.env.PUBLIC_URL}/assets/images/dashboard/64.png`
                  }
                  alt={song.title}
                  style={{
                    borderRadius: "12px",
                    boxShadow: "0 10px 30px rgba(0,0,0,0.5)",
                  }}
                />
              </div>

              <div className="trending-song-detail">
                <div className="main__title main__title--page">
                  <h1>{song.title}</h1>
                  <p className="text-muted">{song.artist}</p>
                </div>

                <div className="release__stat">
                  <span>
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 24 24"
                      width="18"
                      height="18"
                    >
                      <path d="M21.65,2.24a1,1,0,0,0-.8-.23l-13,2A1,1,0,0,0,7,5V15.35A3.45,3.45,0,0,0,5.5,15,3.5,3.5,0,1,0,9,18.5V10.86L20,9.17v4.18A3.45,3.45,0,0,0,18.5,13,3.5,3.5,0,1,0,22,16.5V3A1,1,0,0,0,21.65,2.24ZM5.5,20A1.5,1.5,0,1,1,7,18.5,1.5,1.5,0,0,1,5.5,20Zm13-2A1.5,1.5,0,1,1,20,16.5,1.5,1.5,0,0,1,18.5,18ZM20,7.14,9,8.83v-3L20,4.17Z" />
                    </svg>{" "}
                    {formatDuration(song.duration)} • {releaseYear}
                  </span>
                </div>

                {/* SMART PLAY/PAUSE BUTTON */}
                <div className="d-flex gap-3 align-items-center flex-wrap mt-4">
                  <a
                    href="#!"
                    className="release__buy open-modal d-flex align-items-center gap-2"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      playNow(song._id); // trackId jayega
                    }}
                    style={{
                      padding: "14px 36px",
                      fontSize: "16px",
                      fontWeight: "600",
                      borderRadius: "50px",
                      backgroundColor: "#1DB954",
                      color: "white",
                      transition: "all 0.3s ease",
                    }}
                  >
                    <i
                      className={
                        isCurrentSongPlaying ? "fas fa-pause" : "fas fa-play"
                      }
                    ></i>
                    <span style={{ marginLeft: "8px" }}>
                      {isCurrentSongPlaying ? "Pause" : "Play"}
                    </span>
                  </a>

                  <a
                    href="#!"
                    className={`release__buy open-modal ${likedSongs.has(song._id) ? "liked" : ""}`}
                    onClick={handleLikeSong}
                    style={{ padding: "12px 24px" }}
                  >
                    <i
                      className={
                        likedSongs.has(song._id)
                          ? "fas fa-heart"
                          : "far fa-heart"
                      }
                    ></i>{" "}
                    {likedSongs.has(song._id) ? "Liked" : "Like"}
                  </a>

                  <div className="more-dropdown position-relative">
                    <a
                      href="#!"
                      className="release__buy open-modal more"
                      onClick={toggleDropdown}
                      style={{ padding: "10px 20px", fontSize: "15px" }}
                    >
                      <i className="fa-solid fa-ellipsis"></i> More
                    </a>

                    {openDropdown === "more" && (
                      <div
                        className="dropdown-menu show"
                        style={{
                          position: "absolute",
                          top: "100%",
                          right: 0,
                          zIndex: 9999,
                          background: "#2a2a2a",
                          border: "1px solid #444",
                          borderRadius: "12px",
                          minWidth: "210px",
                          boxShadow: "0 8px 25px rgba(0,0,0,0.6)",
                          marginTop: "8px",
                          padding: "8px 0",
                        }}
                        onClick={(e) => e.stopPropagation()}
                      >
                        <a
                          href="#!"
                          className="dropdown-item"
                          onClick={(e) => {
                            e.preventDefault();
                            handleQueueAction("add", song._id);
                            setOpenDropdown(null);
                          }}
                        >
                          <span
                            className="material-icons"
                            style={{ fontSize: "20px", marginRight: "12px" }}
                          >
                            playlist_add
                          </span>
                          Add to Queue
                        </a>
                        <a
                          href="#!"
                          className="dropdown-item"
                          onClick={openPlaylistModal}
                        >
                          <span
                            className="material-icons"
                            style={{ fontSize: "20px", marginRight: "12px" }}
                          >
                            playlist_add
                          </span>
                          Add to Playlist
                        </a>
                        <a
                          href="#!"
                          className="dropdown-item"
                          onClick={openShare}
                        >
                          <span
                            className="material-icons"
                            style={{ fontSize: "20px", marginRight: "12px" }}
                          >
                            share
                          </span>
                          Share
                        </a>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* SONG DETAILS */}
        <div className="col-12 mt-5">
          <div className="card">
            <div className="card-body">
              <h4 className="card-title mb-4">Song Details</h4>
              <div className="row">
                <div className="col-md-6">
                  <p>
                    <strong>Artist:</strong> {song.artist}
                  </p>
                  <p>
                    <strong>Duration:</strong> {formatDuration(song.duration)}
                  </p>
                  <p>
                    <strong>Release Date:</strong>{" "}
                    {new Date(song.releaseDate).toLocaleDateString()}
                  </p>
                  {song.genre && (
                    <p>
                      <strong>Genre:</strong> {song.genre.join(", ")}
                    </p>
                  )}
                </div>
                <div className="col-md-6">
                  {song.writers && (
                    <p>
                      <strong>Writers:</strong> {song.writers.join(", ")}
                    </p>
                  )}
                  {song.songPublisher && (
                    <p>
                      <strong>Publisher:</strong> {song.songPublisher}
                    </p>
                  )}
                  {song.isrc && (
                    <p>
                      <strong>ISRC:</strong> {song.isrc}
                    </p>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* TOP GENRES SLIDER */}
        <div className="col-12 mt-5">
          <div className="card-header mb-4 d-flex justify-content-between align-items-center">
            <h4 className="card-title text-capitalize">
              top song genres for you
            </h4>
            <div className="common-album position-relative d-lg-block d-none">
              <button
                className="swiper-button-prev"
                onClick={() => sliderRef.current?.slickPrev()}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="18"
                  height="19"
                  viewBox="0 0 18 19"
                  fill="none"
                >
                  <path
                    d="M15.75 9.5C15.75 9.64918 15.6908 9.79226 15.5853 9.89775C15.4798 10.0032 15.3367 10.0625 15.1875 10.0625H4.17025L8.27298 14.1645C8.32525 14.2168 8.3667 14.2788 8.39499 14.3471C8.42327 14.4154 8.43783 14.4886 8.43783 14.5625C8.43783 14.6364 8.42327 14.7096 8.39499 14.7779C8.3667 14.8462 8.32525 14.9082 8.27298 14.9605C8.22072 15.0127 8.15868 15.0542 8.09039 15.0825C8.02211 15.1108 7.94893 15.1253 7.87502 15.1253C7.80111 15.1253 7.72792 15.1108 7.65964 15.0825C7.59135 15.0542 7.52931 15.0127 7.47705 14.9605L2.41455 9.89797C2.36225 9.84573 2.32076 9.78369 2.29245 9.7154C2.26414 9.64712 2.24957 9.57392 2.24957 9.5C2.24957 9.42608 2.26414 9.35288 2.29245 9.2846C2.32076 9.21631 2.36225 9.15427 2.41455 9.10203L7.47705 4.03953C7.58259 3.93398 7.72575 3.87469 7.87502 3.87469C8.02428 3.87469 8.16744 3.93398 8.27298 4.03953C8.37853 4.14508 8.43783 4.28823 8.43783 4.4375C8.43783 4.58677 8.37853 4.72992 8.27298 4.83547L4.17025 8.9375H15.1875C15.3367 8.9375 15.4798 8.99676 15.5853 9.10225C15.6908 9.20774 15.75 9.35082 15.75 9.5Z"
                    fill="#4A525F"
                  />
                </svg>
              </button>
              <button
                className="swiper-button-next"
                onClick={() => sliderRef.current?.slickNext()}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="18"
                  height="19"
                  viewBox="0 0 18 19"
                  fill="none"
                >
                  <path
                    d="M15.5855 9.89797L10.523 14.9605C10.4174 15.066 10.2743 15.1253 10.125 15.1253C9.97573 15.1253 9.83258 15.066 9.72703 14.9605C9.62148 14.8549 9.56219 14.7118 9.56219 14.5625C9.56219 14.4132 9.62148 14.2701 9.72703 14.1645L13.8298 10.0625H2.8125C2.66332 10.0625 2.52024 10.0032 2.41475 9.89775C2.30926 9.79226 2.25 9.64918 2.25 9.5C2.25 9.35082 2.30926 9.20774 2.41475 9.10225C2.52024 8.99676 2.66332 8.9375 2.8125 8.9375H13.8298L9.72703 4.83547C9.62148 4.72992 9.56219 4.58677 9.56219 4.4375C9.56219 4.28823 9.62148 4.14508 9.72703 4.03953C9.83258 3.93398 9.97573 3.87469 10.125 3.87469C10.2743 3.87469 10.4174 3.93398 10.523 4.03953L15.5855 9.10203C15.6378 9.15427 15.6793 9.21631 15.7076 9.2846C15.7359 9.35288 15.7504 9.42608 15.7504 9.5C15.7504 9.57392 15.7359 9.64712 15.7076 9.7154C15.6793 9.78369 15.6378 9.84573 15.5855 9.89797Z"
                    fill="currentColor"
                  />
                </svg>
              </button>
            </div>
          </div>

          <Slider
            ref={sliderRef}
            dots={false}
            infinite={true}
            speed={500}
            slidesToShow={6}
            slidesToScroll={3}
            arrows={false}
            responsive={[
              { breakpoint: 1400, settings: { slidesToShow: 5 } },
              { breakpoint: 1200, settings: { slidesToShow: 4 } },
              { breakpoint: 992, settings: { slidesToShow: 3 } },
              { breakpoint: 768, settings: { slidesToShow: 2 } },
              { breakpoint: 576, settings: { slidesToShow: 1 } },
            ]}
          >
            {genres.map((genre) => (
              <div key={genre._id} className="px-3">
                <div className="position-relative d-inline-block w-100">
                  {/* IMAGE CLICK → playlistId + type: genre */}
                  <div
                    onClick={() => playGenreSongs(genre._id)}
                    style={{ cursor: "pointer" }}
                  >
                    <img
                      src={genre.themeImageUrl || `${process.env.PUBLIC_URL}/img/covers/cover.svg`}
                      alt={genre.name}
                      className="img-fluid rounded-3 mb-3 w-100"
                      style={{
                        height: "180px",
                        objectFit: "cover",
                        transition: "all 0.3s ease",
                      }}
                    />

                    {/* HOVER PLAY BUTTON */}
                    <div
                      className="position-absolute top-50 start-50 translate-middle opacity-0"
                      style={{
                        transition: "all 0.3s ease",
                        pointerEvents: "none",
                        zIndex: 2,
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.opacity = 1;
                        e.currentTarget.previousElementSibling.style.transform =
                          "scale(1.05)";
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.opacity = 0;
                        e.currentTarget.previousElementSibling.style.transform =
                          "scale(1)";
                      }}
                    >
                      <button
                        className="btn btn-success rounded-circle shadow-lg d-flex align-items-center justify-content-center"
                        style={{
                          width: "60px",
                          height: "60px",
                          fontSize: "24px",
                          backgroundColor: "#1DB954",
                          border: "none",
                        }}
                        onClick={(e) => {
                          e.stopPropagation();
                          playGenreSongs(genre._id);
                        }}
                      >
                        Play
                      </button>
                    </div>

                    {/* DARK OVERLAY */}
                    <div
                      className="position-absolute inset-0 rounded-3 opacity-0"
                      style={{
                        background: "rgba(0,0,0,0.5)",
                        transition: "opacity 0.3s ease",
                      }}
                      onMouseEnter={(e) => (e.target.style.opacity = 1)}
                      onMouseLeave={(e) => (e.target.style.opacity = 0)}
                    ></div>
                  </div>

                  {/* TITLE CLICK → NAVIGATE ONLY */}
                  <Link
                    to={`/party-music/${genre._id}`}
                    className="text-decoration-none text-white d-block"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <h6 className="mb-1 text-capitalize">{genre.name}</h6>
                  </Link>
                  <small className="text-muted d-block text-capitalize">
                    {genre.description}
                  </small>
                </div>
              </div>
            ))}
          </Slider>
        </div>
      </div>

      {/* SHARE MODAL */}
      {sharePopup.open && sharePopup.song && (
        <div
          className="modal-overlay"
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(0,0,0,0.8)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 9999,
          }}
          onClick={() => setSharePopup({ open: false, song: null })}
        >
          <div
            className="share-popup"
            style={{
              backgroundColor: "#1a1a1a",
              padding: "25px",
              borderRadius: "12px",
              width: "90%",
              maxWidth: "350px",
              color: "white",
              border: "1px solid #333",
              boxShadow: "0 10px 30px rgba(0,0,0,0.5)",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ textAlign: "center", marginBottom: "25px" }}>
              <h4
                style={{
                  margin: "0 0 10px 0",
                  fontSize: "18px",
                  fontWeight: "600",
                  color: "#aaa7a7",
                }}
              >
                Share
              </h4>
              <p style={{ margin: 0, color: "#ccc", fontSize: "14px" }}>
                {song.title}
              </p>
            </div>
            <div
              style={{
                display: "flex",
                justifyContent: "center",
                gap: "20px",
                marginBottom: "25px",
                flexWrap: "wrap",
              }}
            >
              <button
                onClick={() => shareOnSocialMedia("facebook")}
                style={{
                  background: "none",
                  border: "none",
                  cursor: "pointer",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  gap: "8px",
                  fontSize: "12px",
                  color: "#ccc",
                }}
              >
                <div
                  style={{
                    width: "50px",
                    height: "50px",
                    backgroundColor: "#1877f2",
                    borderRadius: "50%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <i
                    className="fab fa-facebook-f"
                    style={{ color: "white", fontSize: "20px" }}
                  ></i>
                </div>
                Facebook
              </button>
              <button
                onClick={() => shareOnSocialMedia("twitter")}
                style={{
                  background: "none",
                  border: "none",
                  cursor: "pointer",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  gap: "8px",
                  fontSize: "12px",
                  color: "#ccc",
                }}
              >
                <div
                  style={{
                    width: "50px",
                    height: "50px",
                    backgroundColor: "#000000",
                    borderRadius: "50%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <i
                    className="fab fa-x-twitter"
                    style={{ color: "white", fontSize: "20px" }}
                  ></i>
                </div>
                Twitter
              </button>
              <button
                onClick={() => shareOnSocialMedia("whatsapp")}
                style={{
                  background: "none",
                  border: "none",
                  cursor: "pointer",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  gap: "8px",
                  fontSize: "12px",
                  color: "#ccc",
                }}
              >
                <div
                  style={{
                    width: "50px",
                    height: "50px",
                    backgroundColor: "#25d366",
                    borderRadius: "50%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <i
                    className="fab fa-whatsapp"
                    style={{ color: "white", fontSize: "20px" }}
                  ></i>
                </div>
                WhatsApp
              </button>
              <button
                onClick={() => shareOnSocialMedia("telegram")}
                style={{
                  background: "none",
                  border: "none",
                  cursor: "pointer",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  gap: "8px",
                  fontSize: "12px",
                  color: "#ccc",
                }}
              >
                <div
                  style={{
                    width: "50px",
                    height: "50px",
                    backgroundColor: "#0088cc",
                    borderRadius: "50%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <i
                    className="fab fa-telegram-plane"
                    style={{ color: "white", fontSize: "20px" }}
                  ></i>
                </div>
                Telegram
              </button>
            </div>
            <div style={{ display: "flex", gap: "10px", marginBottom: "20px" }}>
              <input
                type="text"
                value={generateShareableUrl(song._id)}
                readOnly
                style={{
                  flex: 1,
                  padding: "12px 15px",
                  backgroundColor: "#2a2a2a",
                  border: "1px solid #444",
                  borderRadius: "8px",
                  color: "white",
                  fontSize: "14px",
                }}
              />
              <button
                onClick={copyLink}
                style={{
                  padding: "12px 20px",
                  backgroundColor: "#1DB954",
                  color: "white",
                  border: "none",
                  borderRadius: "8px",
                  cursor: "pointer",
                  fontWeight: "600",
                }}
              >
                COPY
              </button>
            </div>
            <button
              onClick={() => setSharePopup({ open: false, song: null })}
              style={{
                width: "100%",
                padding: "12px",
                backgroundColor: "transparent",
                color: "#ccc",
                border: "1px solid #444",
                borderRadius: "8px",
                cursor: "pointer",
              }}
            >
              CANCEL
            </button>
          </div>
        </div>
      )}

      {/* PLAYLIST MODAL */}
      {playlistModal.open && (
        <div
          className="modal-overlay"
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(0,0,0,0.8)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 9999,
          }}
        >
          <div
            className="playlist-popup"
            style={{
              backgroundColor: "#1a1a1a",
              padding: "25px",
              borderRadius: "12px",
              width: "90%",
              maxWidth: "400px",
              color: "white",
              border: "1px solid #333",
              boxShadow: "0 10px 30px rgba(0,0,0,0.5)",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ textAlign: "center", marginBottom: "20px" }}>
              <h4
                style={{
                  margin: "0 0 12px 0",
                  fontSize: "20px",
                  fontWeight: "600",
                }}
              >
                Add to Playlist
              </h4>
              <p style={{ margin: 0, color: "#ccc", fontSize: "16px" }}>
                {song.title}
              </p>
            </div>

            {!showCreateForm ? (
              <>
                <div
                  style={{
                    maxHeight: "250px",
                    overflowY: "auto",
                    marginBottom: "25px",
                  }}
                >
                  {playlists.length > 0 ? (
                    playlists.map((p) => {
                      const checked = selectedPlaylists.has(p._id);
                      return (
                        <label
                          key={p._id}
                          style={{
                            display: "flex",
                            alignItems: "center",
                            padding: "16px",
                            border: "1px solid #333",
                            borderRadius: "14px",
                            marginBottom: "12px",
                            cursor: "pointer",
                            backgroundColor: checked ? "#1DB95425" : "#222",
                            transition: "all 0.2s",
                          }}
                        >
                          <input
                            type="checkbox"
                            checked={checked}
                            onChange={() => togglePlaylistSelection(p._id)}
                            style={{
                              marginRight: "16px",
                              transform: "scale(1.3)",
                            }}
                          />
                          <img
                            src={
                              p.playlistCoverUrl || "/img/live/songDefault.png"
                            }
                            alt={p.name}
                            style={{
                              width: "50px",
                              height: "50px",
                              borderRadius: "10px",
                              marginRight: "16px",
                              objectFit: "cover",
                            }}
                          />
                          <div style={{ flex: 1 }}>
                            <div
                              style={{ fontWeight: "600", fontSize: "16px" }}
                            >
                              {p.name}
                            </div>
                            {p.description && (
                              <small style={{ color: "#aaa" }}>
                                {p.description}
                              </small>
                            )}
                          </div>
                          {checked && (
                            <i
                              className="fas fa-check text-success ms-2"
                              style={{ fontSize: "20px" }}
                            ></i>
                          )}
                        </label>
                      );
                    })
                  ) : (
                    <div
                      style={{
                        textAlign: "center",
                        padding: "40px",
                        color: "#888",
                      }}
                    >
                      No playlists yet
                    </div>
                  )}
                </div>

                <div className="d-flex gap-3">
                  <button
                    onClick={addToSelectedPlaylists}
                    disabled={selectedPlaylists.size === 0 || playlistLoading}
                    style={{
                      flex: 1,
                      padding: "16px",
                      backgroundColor: "#1DB954",
                      color: "white",
                      border: "none",
                      borderRadius: "14px",
                      cursor:
                        selectedPlaylists.size === 0
                          ? "not-allowed"
                          : "pointer",
                      fontWeight: "700",
                      opacity: selectedPlaylists.size === 0 ? 0.5 : 1,
                    }}
                  >
                    {playlistLoading
                      ? "Adding..."
                      : `Add to ${selectedPlaylists.size} Playlist${selectedPlaylists.size > 1 ? "s" : ""}`}
                  </button>
                  <button
                    onClick={() => setShowCreateForm(true)}
                    style={{
                      flex: 1,
                      padding: "16px",
                      backgroundColor: "transparent",
                      color: "#ccc",
                      border: "1px solid #555",
                      borderRadius: "14px",
                      cursor: "pointer",
                      fontWeight: "600",
                    }}
                  >
                    Create New
                  </button>
                </div>
              </>
            ) : (
              <>
                <input
                  className="form-control mb-3"
                  placeholder="Playlist Name *"
                  value={newPlaylist.name}
                  onChange={(e) =>
                    setNewPlaylist((prev) => ({
                      ...prev,
                      name: e.target.value,
                    }))
                  }
                  style={{
                    backgroundColor: "#2a2a2a",
                    border: "1px solid #444",
                    color: "white",
                    padding: "16px",
                    borderRadius: "12px",
                  }}
                />
                <textarea
                  className="form-control mb-3"
                  placeholder="Description (optional)"
                  rows="3"
                  value={newPlaylist.description}
                  onChange={(e) =>
                    setNewPlaylist((prev) => ({
                      ...prev,
                      description: e.target.value,
                    }))
                  }
                  style={{
                    backgroundColor: "#2a2a2a",
                    border: "1px solid #444",
                    color: "white",
                    padding: "16px",
                    borderRadius: "12px",
                    resize: "none",
                  }}
                />
                <input
                  type="file"
                  accept="image/*"
                  className="form-control mb-4"
                  onChange={(e) =>
                    setNewPlaylist((prev) => ({
                      ...prev,
                      coverImage: e.target.files[0],
                    }))
                  }
                  style={{
                    backgroundColor: "#2a2a2a",
                    border: "1px solid #444",
                    color: "white",
                    padding: "12px",
                  }}
                />

                <div className="d-flex gap-3">
                  <button
                    onClick={createNewPlaylist}
                    disabled={playlistLoading}
                    style={{
                      flex: 1,
                      padding: "16px",
                      backgroundColor: "#1DB954",
                      color: "white",
                      border: "none",
                      borderRadius: "14px",
                      cursor: "pointer",
                      fontWeight: "700",
                    }}
                  >
                    {playlistLoading ? "Creating..." : "Create & Add"}
                  </button>
                  <button
                    onClick={() => setShowCreateForm(false)}
                    style={{
                      flex: 1,
                      padding: "16px",
                      backgroundColor: "transparent",
                      color: "#ccc",
                      border: "1px solid #555",
                      borderRadius: "14px",
                      cursor: "pointer",
                      fontWeight: "600",
                    }}
                  >
                    Cancel
                  </button>
                </div>
              </>
            )}

            <button
              onClick={() => setPlaylistModal({ open: false, song: null })}
              style={{
                width: "100%",
                marginTop: "20px",
                padding: "16px",
                backgroundColor: "transparent",
                color: "#ccc",
                border: "1px solid #555",
                borderRadius: "14px",
                cursor: "pointer",
                fontSize: "16px",
              }}
            >
              CLOSE
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default Song;
